import { createContext, useContext, useCallback, useMemo, useLayoutEffect, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Kbd } from '../ui/Kbd';

interface PanelSelection { id: string; title: string; trigger: HTMLElement; focusRequest: number }
const CockpitPanelContext = createContext<{
  selection: PanelSelection | null; host: HTMLDivElement | null;
  open: (id: string, title: string, trigger: HTMLElement, focus?: boolean, onOpened?: () => void) => void;
  close: () => void;
  release: (id: string) => void;
} | null>(null);

/** One shell for the app. Content slots remain mounted when another owner takes over. */
export function CockpitPanelProvider({ children, routeKey }: { children: ReactNode; routeKey?: string }) {
  const [selection, setSelection] = useState<PanelSelection | null>(null);
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const open = useCallback((id: string, title: string, trigger: HTMLElement, focus = true, onOpened?: () => void) => {
    const proceed = () => {
      onOpened?.();
      setSelection(current => ({ id, title, trigger, focusRequest: (current?.focusRequest ?? 0) + Number(focus) }));
    };
    const guard = Array.from(document.querySelectorAll('.cockpit-side-panel [data-panel-close-guard]')).find(element => !element.closest('[hidden]'));
    if (guard && !guard.dispatchEvent(new CustomEvent('panel-request-close', { cancelable: true, detail: { proceed } }))) return;
    proceed();
  }, []);
  const close = useCallback(() => setSelection(null), []);
  const release = useCallback((id: string) => setSelection(current => current?.id === id ? null : current), []);
  useEffect(() => {
    setSelection(current => current?.id === 'paula' ? current : null);
  }, [routeKey]);
  const value = useMemo(() => ({ selection, host, open, close, release }), [selection, host, open, close, release]);
  return <CockpitPanelContext.Provider value={value}>{children}
    <SidePanel open={!!selection} title={selection?.title ?? ''} returnFocus={selection?.trigger} focusRequest={selection?.focusRequest} onClose={close} keepMounted>
      <div ref={setHost} className="h-full min-h-0 flex-1 overflow-auto" />
    </SidePanel>
  </CockpitPanelContext.Provider>;
}
export const useCockpitPanel = () => useContext(CockpitPanelContext);
export function PanelContent({ id, children }: { id: string; children: ReactNode }) {
  const panel = useCockpitPanel();
  const release = panel?.release;
  useLayoutEffect(() => () => { release?.(id); }, [id, release]);
  return panel?.host ? createPortal(<div hidden={panel.selection?.id !== id} className="h-full min-h-0">{children}</div>, panel.host) : null;
}

/** Portaled overlay: never changes the cockpit's container width or column rules. */
export function SidePanel({ open, title, children, onClose, returnFocus, keepMounted = false, focusRequest = 0 }: {
  open: boolean; title: string; children: ReactNode; onClose: () => void; returnFocus?: HTMLElement | null; keepMounted?: boolean; focusRequest?: number;
}) {
  const titleId = useId();
  const [modal, setModal] = useState(() => window.innerWidth < 768);
  const [top, setTop] = useState(0);
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      setModal(window.innerWidth < 768);
      setTop(document.querySelector('[data-navigation-header]')?.getBoundingClientRect().bottom ?? 48);
    };
    measure();
    const header = document.querySelector('[data-navigation-header]');
    const observer = !header || typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (header) observer?.observe(header);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [open]);
  const panel = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const trigger = useRef<HTMLElement | null>(null);
  if (open && returnFocus) trigger.current = returnFocus;
  const requestClose = () => {
    const guard = Array.from(panel.current?.querySelectorAll('[data-panel-close-guard]') ?? []).find(element => !element.closest('[hidden]'));
    if (guard && !guard.dispatchEvent(new CustomEvent('panel-request-close', { cancelable: true, detail: { proceed: () => close.current() } }))) return;
    close.current();
  };
  const request = useRef(requestClose);
  request.current = requestClose;
  useLayoutEffect(() => {
    if (!open) return;
    trigger.current ??= returnFocus ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const surface = panel.current!;
    const controls = Array.from(surface.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href], [tabindex="0"]')).filter(control => !control.closest('[hidden]'));
    (controls.find(control => control.matches('textarea, input')) ?? controls[0] ?? surface).focus();
  }, [open, focusRequest]);
  useEffect(() => {
    if (!open) return;
    trigger.current ??= returnFocus ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const surface = panel.current!;
    const visibleControls = () => Array.from(surface.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href], [tabindex="0"]')).filter(control => !control.closest('[hidden]'));
    const keyboard = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.target instanceof Element && event.target.closest('[role="dialog"], [role="listbox"], [role="menu"], [popover]') && !surface.contains(event.target)) return;
      if (document.querySelector('[aria-modal="true"]:not(.cockpit-side-panel), .cockpit-modal-layer')) return;
      if (event.target instanceof Element && event.target.getAttribute('aria-expanded') === 'true') return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); request.current(); }
      if (event.key === 'Tab' && window.innerWidth < 768) {
        const controls = visibleControls();
        const first = controls[0]; const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', keyboard, true);
    return () => { document.removeEventListener('keydown', keyboard, true); if (trigger.current?.isConnected && (!document.activeElement || document.activeElement === document.body || surface.contains(document.activeElement))) trigger.current.focus({ preventScroll: true }); trigger.current = null; };
  }, [open]);
  if (!open && !keepMounted) return null;
  return createPortal(<div hidden={!open} className={`fixed inset-0 cockpit-panel-layer ${modal ? 'bg-black/20' : 'pointer-events-none'}`}><section ref={panel} style={{ top: modal ? 0 : top }} role={modal ? 'dialog' : 'complementary'} aria-modal={modal ? true : undefined} aria-labelledby={titleId} className="cockpit-side-panel pointer-events-auto flex flex-col border border-line bg-panel text-ink">
    <header role="presentation" className="flex shrink-0 items-center justify-between gap-3 border-b border-line p-4"><h2 id={titleId} title={title} className="min-w-0 flex-1 line-clamp-2 text-base font-semibold [overflow-wrap:anywhere]">{title}</h2><button type="button" onClick={() => request.current()} className="shrink-0 min-h-6 min-w-6 rounded-ctl border border-line px-2 py-1 text-sm max-md:min-h-11">Schließen <Kbd>Esc</Kbd></button></header>
    <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4 [overflow-wrap:anywhere]">{children}</div>
  </section></div>, document.body);
}
